import { WORKSPACE_LIMITS } from "../../../constant/workspaceLimits";
import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import JuniorShimmerLoader from "./JuniorShimmerLoader";

const JuniorWorkspace = ({
  mode = "free_play",
  subMode = "basic", // "basic" (ages 5-7, icon-only) | "advanced" (ages 7-10, icon+text)
  initialData = null,
  selectedChild = null,
  readOnly = false,
  onCodeChange = null,
  onGradeSubmit = null,
  onSaveSprite = null,
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isIframeReady, setIsIframeReady] = useState(false);
  const [activeSubMode, setActiveSubMode] = useState(subMode);
  const iframeRef = useRef(null);
  const hasLoadedInitialData = useRef(false);

  const onSaveSpriteRef = useRef(onSaveSprite);
  useEffect(() => {
    onSaveSpriteRef.current = onSaveSprite;
  }, [onSaveSprite]);

  useEffect(() => {
    // Safety fallback timer for shimmer transition (in case iframe onLoad event fails)
    const timer = setTimeout(() => {
      setIsLoaded(true);
    }, 1200);
    return () => clearTimeout(timer);
  }, []);

  // Send initialData to iframe when available - strictly single execution guarded by hasLoadedInitialData
  const sendInitialDataToIframe = () => {
    if (!initialData || !iframeRef.current || !iframeRef.current.contentWindow) return;
    if (initialData instanceof ArrayBuffer) {
      iframeRef.current.contentWindow.postMessage(
        { type: "LOAD_PROJECT_BUFFER", payload: { buffer: initialData } },
        "*"
      );
    } else if (typeof initialData === "string" && (initialData.startsWith("data:") || initialData.startsWith("UEsDB"))) {
      iframeRef.current.contentWindow.postMessage(
        { type: "LOAD_PROJECT_JSON", payload: initialData },
        "*"
      );
    } else {
      iframeRef.current.contentWindow.postMessage(
        { type: "LOAD_PROJECT", payload: initialData },
        "*"
      );
    }
  };

  // Dispatch initial project payload ONLY once Scratch VM in iframe is fully ready
  useEffect(() => {
    if (initialData && isIframeReady && !hasLoadedInitialData.current) {
      hasLoadedInitialData.current = true;
      sendInitialDataToIframe();
    }
  }, [initialData, isIframeReady]);

  // If initialData is null (e.g. New Project), mark as loaded so user edits are captured
  useEffect(() => {
    if (!initialData && isIframeReady) {
      hasLoadedInitialData.current = true;
    }
  }, [initialData, isIframeReady]);

  // Listen to messages from the Junior Studio iframe
  useEffect(() => {
    const handleMessage = (event) => {
      if (!event.data) return;
      const { type, payload } = event.data;

      if (
        type === "JUNIOR_READY" ||
        type === "SEND_PROJECT_JSON" ||
        type === "SCRATCH_PROJECT_CHANGE"
      ) {
        setIsIframeReady(true);
      }

      if (type === "JUNIOR_SUBMODE_CHANGED" && payload?.mode) {
        setActiveSubMode(payload.mode);
      }

      if (type === "TRIGGER_CLOUD_SAVE") {
        // Request fresh project JSON and SB3 from iframe for saving
        if (iframeRef.current && iframeRef.current.contentWindow) {
          iframeRef.current.contentWindow.postMessage({ type: "GET_PROJECT_JSON" }, "*");
        }
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent("workspace-save-shortcut"));
        }, 80);
      }

      if (type === "TRIGGER_SAVE_SCRATCH_SPRITE") {
        if (onSaveSpriteRef.current) {
          onSaveSpriteRef.current({
            spriteCode: payload?.spriteCode,
            thumbnail: payload?.thumbnail,
            defaultName: payload?.defaultName,
          });
        }
      }

      if (type === "SPRITE_ADDED_SUCCESS") {
        toast.success(
          `Sprite "${payload?.name || "Sprite"}" added to project!`
        );
      }

      if ((type === "SEND_PROJECT_JSON" || type === "SCRATCH_PROJECT_CHANGE") && onCodeChange) {
        const targetEnv = subMode === "advanced" ? "junior_blocks_adv" : "junior_blocks_basic";
        onCodeChange(payload, targetEnv);
      }

      if (type === "LOAD_ERROR") {
        toast.error(`Failed to load Junior Blocks project: ${payload?.error || "Corrupted project file"}`);
      }

      if (type === "ASSET_SIZE_LIMIT_EXCEEDED") {
        const { assetType, assetName, incomingSizeBytes } = payload || {};
        const label = assetName ? `"${assetName}"` : (assetType ? assetType.toLowerCase() : "asset");
        const formattedIncoming = incomingSizeBytes ? ` (${(incomingSizeBytes / 1024).toFixed(1)} KB)` : "";
        toast.warning(
          `Project limit (${payload && payload.maxLimitBytes ? (payload.maxLimitBytes >= 1024 * 1024 ? (payload.maxLimitBytes / (1024 * 1024)).toFixed(0) + " MB" : Math.round(payload.maxLimitBytes / 1024) + " KB") : "500 KB"}) reached! Adding ${label}${formattedIncoming} would exceed the allowed project size. Please choose a smaller file or delete unused sprites/costumes.`,
          { autoClose: 6000 }
        );
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [onCodeChange, subMode]);

  // Sync subMode when prop changes
  useEffect(() => {
    if (iframeRef.current && iframeRef.current.contentWindow) {
      iframeRef.current.contentWindow.postMessage(
        { type: "SET_JUNIOR_SUBMODE", payload: { mode: subMode } },
        "*"
      );
    }
  }, [subMode]);

  return (
    <div className="w-full h-full bg-[#FAF6EE] overflow-hidden relative flex flex-col select-none min-h-[640px]">
      <JuniorShimmerLoader
        subMode={activeSubMode || subMode}
        isLoaded={isLoaded}
      />

      <iframe
        ref={iframeRef}
        title="Junior Blocks"
        src={`/scratch/junior.html?mode=${subMode || "basic"}&maxProjectLimitBytes=${WORKSPACE_LIMITS.WORKSPACE_MEMORY_LIMIT_BYTES}`}
        loading="eager"
        className="w-full h-full border-0 block flex-1 min-h-[640px]"
        onLoad={() => {
          setIsLoaded(true);
          if (iframeRef.current && iframeRef.current.contentWindow) {
            iframeRef.current.contentWindow.postMessage(
              { type: "SET_JUNIOR_SUBMODE", payload: { mode: subMode } },
              "*"
            );
            if (!initialData) {
              // Immediately populate activeWorkspaceCode so Save to Cloud is available
              iframeRef.current.contentWindow.postMessage({ type: "GET_PROJECT_JSON" }, "*");
            }
          }
        }}
        allow="fullscreen; autoplay"
      />
    </div>
  );
};

export default JuniorWorkspace;
